import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Edit3, Save, History, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HumanValidationPanel = ({ documentId, ocrResult, onValidated }) => {
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [corrections, setCorrections] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const token = localStorage.getItem('reasondms_token');

  useEffect(() => {
    if (ocrResult?.full_text) {
      setEditedText(ocrResult.full_text);
    }
  }, [ocrResult]);

  const handleStartEdit = () => {
    setEditing(true);
    setEditedText(ocrResult?.full_text || '');
  };

  const handleSave = async (approved) => {
    setSaving(true);
    try {
      // Compute corrections
      const original = ocrResult?.full_text || '';
      const edited = editedText;
      const correctionsList = {};
      if (original !== edited) {
        correctionsList.text_correction = {
          original: original.substring(0, 200),
          corrected: edited.substring(0, 200),
          full_corrected_text: edited
        };
      }

      const response = await fetch(`${API_URL}/api/vision/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          doc_id: documentId || 'manual',
          result_id: ocrResult?.processed_at || new Date().toISOString(),
          corrections: correctionsList,
          approved
        })
      });

      if (!response.ok) {
        let errorMsg = 'Validation submission failed';
        try { errorMsg = (await response.json()).detail || errorMsg; } catch { errorMsg = response.status === 404 ? 'Vision AI service not available' : `Server error (${response.status})`; }
        throw new Error(errorMsg);
      }
      const data = await response.json();
      toast.success(approved ? 'OCR result approved!' : 'Corrections submitted for review');
      setEditing(false);
      onValidated?.(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vision/validation-history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.records || []);
        setShowHistory(true);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  if (!ocrResult?.full_text) {
    return null;
  }

  return (
    <Card data-testid="human-validation-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-orange-600" />
              Human Validation
            </CardTitle>
            <CardDescription className="text-xs">Review and correct OCR results</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="text-xs" onClick={loadHistory}>
              <History className="h-3 w-3 mr-1" /> History
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {editing ? (
          <>
            <textarea
              data-testid="validation-text-editor"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className="w-full h-40 text-sm p-3 border rounded-lg font-mono resize-y focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
              placeholder="Edit OCR text..."
            />
            <div className="flex justify-end gap-2">
              <Button
                data-testid="validation-cancel-btn"
                variant="ghost"
                size="sm"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="validation-reject-btn"
                variant="outline"
                size="sm"
                onClick={() => handleSave(false)}
                disabled={saving}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                Submit Corrections
              </Button>
              <Button
                data-testid="validation-approve-btn"
                size="sm"
                onClick={() => handleSave(true)}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                Approve
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <div className="bg-slate-50 rounded-lg p-3 max-h-32 overflow-y-auto text-sm">
              {ocrResult.full_text.substring(0, 300)}{ocrResult.full_text.length > 300 ? '...' : ''}
            </div>
            <Button
              data-testid="validation-edit-btn"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleStartEdit}
            >
              <Edit3 className="h-3 w-3 mr-2" />
              Review & Edit OCR Result
            </Button>
          </div>
        )}

        {/* History */}
        {showHistory && history.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <span className="text-xs font-medium text-slate-500">Validation History</span>
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded">
                <span>{new Date(h.created_at).toLocaleDateString()}</span>
                <Badge variant="outline" className={h.approved ? 'text-emerald-600' : 'text-amber-600'}>
                  {h.approved ? 'Approved' : 'Corrected'}
                </Badge>
                <span>{h.corrections_count || Object.keys(h.corrections || {}).length} fixes</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HumanValidationPanel;
